import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie';
import { resolve } from 'core-js/fn/promise';
import { Router } from '@angular/router';

export enum tooltipPreferenceOptions {
	FLOATING = 'floating',
	FIXED = 'fixed'
}

@Injectable({
	providedIn: 'root'
})
export class AppConfigService {
	private appConfig;				// contents of the config file - whether that be the original or the secondary config
	private secondaryConfigFilename = "";	// secondary configuration file name specified by config=<alt config>.json
	// based on DemoMode, systemConfiguration.OperatorInterface or systemConfiguration.OperatorInterfaceDemoMode
	OperatorInterfaceConfiguration: any = {};
	private baseDataDir: string;	// baseDataDir based on demo mode
	private baseDir: string;		// baseDir based on demo mode
	private usersURL: string;		// url to user web services
	private adminwsURL: string;		// url to admin web services
	private isGuestAllowed;			// is true, login not required
	private userName;				// username from cookie service
	private userPassword;			// password from cookie service
	private cookieCredentialsExist = false;	// do cookies exist defining username and password
	private cookiesAcknowledged = false;	// cookies acknowledged from cookie service
	private disclaimerRead = false;			// disclaimerRead from cookie service
	private displayFloodImpactOnMap = false;		// is flood impact enabled enabled for the map display?
	private displayFloodImpactSummary = false;		// is flood impact menu item visible?
	private chartTooltipPreference: string = tooltipPreferenceOptions.FLOATING;		// is tooltip location tracking the mouse or fixed

	constructor( private http: HttpClient, 
			private router: Router,
			private location: Location,
			private cookieService: CookieService ) { }

	loadAppConfig() {
		const path = this.location.path(false);
		console.log(path);
		let configurationFile = 'nsoperator-config.json';		// default config filename
		if ( path.includes("config=") ) {
			let fn = path.substring(path.indexOf("config=")+7);
			// make sure configuration ends with .json 
			this.secondaryConfigFilename = fn + (fn.endsWith(".json") ? "" : ".json");
			configurationFile = this.secondaryConfigFilename;
		}
		console.log('Looking for the configuration file ' + configurationFile );
		return this.http.get("assets/" + configurationFile)
			.toPromise()
			.then( data => {
				this.processCookies();
				this.initializeSystem(data);
			});
	}

	initializeSystem (configuration) {
		this.appConfig = configuration;
		// console.log("Initializing system using configuration: ", configuration);
		this.verifySystemConfigFormatting(configuration);
		this.processSystemConfiguration(configuration)
		this.setIfGuestIsAllowed(configuration);
		this.setIfFloodImpactIsDisplayed(configuration);
	}


	getConfig() {
		return this.appConfig;
	}

	getBaseDataDir() {
		return this.baseDataDir;

	}

	getBaseDir() {
		return this.baseDir;
	}

	getChartTooltipPreference() {
		return this.chartTooltipPreference;
	}

	getUsersURL() {
		return this.usersURL;
	}

	getAdminWSURL() {
		return this.adminwsURL;
	}

	getIfGuestIsAllowed() {
		return this.isGuestAllowed;
	}

	getDisclaimerRead() {
		return this.disclaimerRead;
	}
	getCookiesAcknowledged() {
		return this.cookiesAcknowledged;
	}

	getOperatorInterfaceConfiguration() {
		return this.OperatorInterfaceConfiguration;
	}

	getUserName() {
		return this.userName;
	}
	getUserPassword() {
		return this.userPassword;
	}

	// only allow queryParam "config"
	goToRouterLink(linkString: string) {
		console.log(linkString);
		const link = [linkString];
		let queryParams = {};

		if ( this.secondaryConfigFilename != "") {
			queryParams['config'] = this.secondaryConfigFilename;
		}
		console.log(link, queryParams);
		this.router.navigate(link, { queryParams: queryParams});
	}

	processCookies() {
		this.userName = this.cookieService.get('NOIUserName');
		this.userPassword = this.cookieService.get('NOIUserPassword');
		if ( this.userName != undefined && this.userPassword != undefined ) {
			this.cookieCredentialsExist = true;
		}
		this.cookiesAcknowledged = this.cookieService.get('NOICookiesAcknowledged') == "true" ? true : false;
		this.disclaimerRead = this.cookieService.get('NOIDisclaimerRead') == "true" ? true : false;
		const ctp = this.cookieService.get("NOIChartTooltipPreference");
		this.chartTooltipPreference = ctp == undefined  ? tooltipPreferenceOptions.FLOATING : ctp;
		console.log("username: " + this.userName, "disclaimerRead: " + this.disclaimerRead);
	}

	getAdditionalDataDisplayLayers() {
		/*
		if (  this.appConfig?.DemoMode  && 
					this.appConfig?.OperatorInterfaceDemoMode.hasOwnProperty('additionalDataDisplayLayers')) {
			return this.appConfig.OperatorInterfaceDemoMode.additionalDataDisplayLayers;
		} else if (  !this.appConfig?.DemoMode  && 
					this.appConfig.OperatorInterface.hasOwnProperty('additionalDataDisplayLayers')) {
			return this.appConfig.OperatorInterface.additionalDataDisplayLayers;
		} else {
			return [];
		}
			*/
		if (  this.OperatorInterfaceConfiguration.hasOwnProperty('additionalDataDisplayLayers')) {
			return this.OperatorInterfaceConfiguration.additionalDataDisplayLayers;
		} else {
			return [];
		}
	}

	getDisplayFloodImpactOnMap() {
		return this.displayFloodImpactOnMap;
	}

	getDisplayFloodImpactSummary() {
		return this.displayFloodImpactSummary;
	}

	getDocumentationUrl() {
		if ( !this.appConfig.hasOwnProperty ('Documentation')) {
			return 'assets/404.html';
		}
		if ( !this.appConfig.Documentation.hasOwnProperty ('IP') ||
			!this.appConfig.Documentation.hasOwnProperty('userPath')) {
			return 'assets/404.html';
		}

		const documentationUrl = this.appConfig.Documentation.IP + '/' + this.appConfig.Documentation.userPath;
		if ( documentationUrl.length === 1 ) {
			return 'assets/404.html';
		} else {
			return documentationUrl;
		}
	}

	getClientLogoURL () {
		if ( this.appConfig.hasOwnProperty('ClientLogo')) {
			return this.appConfig.ClientLogo;
		} else {
			return '';
		}
	}

	getClientWebsite () {
		if ( this.appConfig.hasOwnProperty('ClientWebsite')) {
			return this.appConfig.ClientWebsite;
		} else {
			return 'https://trilynx.systems';
		}
	}

	// Retrieve a value from BridgeDashboardPreferences
	// If that preference doesn't exist, return the given defaultValue
	getDashboardPreference( preference, defaultValue ) {
		if ( this.appConfig.hasOwnProperty ('DashboardPreferences') &&
			this.appConfig.DashboardPreferences.hasOwnProperty(preference)) {
				return this.appConfig.DashboardPreferences[preference];
			}
		else {
			return defaultValue;
		}
	}

	getDataWebServicesURL() {
    	return this.appConfig.WebServices.dataIP + '/' +
				this.appConfig.WebServices.dataRootURI + '/';
	}

	// Return the standard dashboard configuration or an empty array if not defined
	getStandardDashboardConfiguration() {
		if ( this.appConfig.hasOwnProperty('StandardDashboardConfiguration')) {
			return this.appConfig.StandardDashboardConfiguration;
		} else {
			return [];
		}
	}

	// sets both if flood impact map and summary are displayed
	setIfFloodImpactIsDisplayed(config) {
		if ( config.DemoMode ) {
			// display flood impact on map
			if ( config.OperatorInterfaceDemoMode.hasOwnProperty ( 'floodImpactLayer' ) &&
				config.OperatorInterfaceDemoMode.floodImpactLayer.hasOwnProperty ( 'displayOnMap' ) &&
				config.OperatorInterfaceDemoMode.floodImpactLayer.displayOnMap === true ) {
					this.displayFloodImpactOnMap = true;
			}
			// display flood impact summary
			if ( config.OperatorInterfaceDemoMode.hasOwnProperty ( 'floodImpactLayer' ) &&
				config.OperatorInterfaceDemoMode.floodImpactLayer.hasOwnProperty ( 'displayFloodImpactSummary' ) &&
				config.OperatorInterfaceDemoMode.floodImpactLayer.displayFloodImpactSummary === true ) {
					this.displayFloodImpactSummary = true;
			}
		} else {
			// display flood impact on map
			if ( config.OperatorInterface.hasOwnProperty ( 'floodImpactLayer' ) &&
				config.OperatorInterface.floodImpactLayer.hasOwnProperty ( 'displayOnMap' ) &&
				config.OperatorInterface.floodImpactLayer.displayOnMap === true ) {
					this.displayFloodImpactOnMap = true;
			}
			// display flood impact summary
			if ( config.OperatorInterface.hasOwnProperty ( 'floodImpactLayer' ) &&
				config.OperatorInterface.floodImpactLayer.hasOwnProperty ( 'displayFloodImpactSummary' ) &&
				config.OperatorInterface.floodImpactLayer.displayFloodImpactSummary === true ) {
					this.displayFloodImpactSummary = true;
			}
		}

	}

	setIfGuestIsAllowed(config) {
		if ( config.DemoMode ) {
			if ( config.OperatorInterfaceDemoMode.hasOwnProperty ( 'defaultGuestLogin' ) &&
				config.OperatorInterfaceDemoMode.defaultGuestLogin === true ) {
					this.isGuestAllowed = true;
			} else {
					this.isGuestAllowed = false;
			}
		} else {
			if ( config.OperatorInterface.hasOwnProperty ( 'defaultGuestLogin' ) &&
				config.OperatorInterface.defaultGuestLogin === true ) {
					this.isGuestAllowed = true;
			} else {
					this.isGuestAllowed = false;
			}
		}
	}

	setCookiesAcknowledged( cookiesOK: boolean ) {
		const date = this.getDateYearAhead();
		this.cookieService.put('NOICookiesAcknowledged', cookiesOK ? 'true' : 'false', { expires: date });
	}

	setDisclaimerRead() {
		const date = this.getDateYearAhead();
		this.cookieService.put('NOIDisclaimerRead', 'true', { expires: date });
	}

	setChartTooltipPreference(ttp: string ) {
		this.chartTooltipPreference = ttp;
		const date = this.getDateYearAhead();
		this.cookieService.put('NOIChartTooltipPreference', ttp, { expires: date });
	}

	clearCookies() {
		this.cookieService.removeAll();
	}

	getDateYearAhead() {
		const newDate = new Date()
		newDate.setFullYear(newDate.getFullYear() + 1);
		return newDate;
	}

	// Set baseDir and BaseDataDir
	processSystemConfiguration(config) {
		if ( config.DemoMode ) {
			this.OperatorInterfaceConfiguration = config.OperatorInterfaceDemoMode;
			// just to make this routine more readable
			const conf = this.OperatorInterfaceConfiguration;

			// external data directory contains the date portion in a directory
			const periodEndDate = conf.periodEndDate;
			const date = periodEndDate.slice(0, periodEndDate.search('T'));

			this.baseDataDir = conf.baseDir + conf.dataDir;
			this.baseDir = conf.baseDir + conf.externalDataDir + date + '/';
		} else {
			this.OperatorInterfaceConfiguration = config.OperatorInterface;
			// just to make this routine more readable
			const conf = this.OperatorInterfaceConfiguration;

			this.baseDataDir = conf.baseDir + conf.dataDir;
			this.baseDir = conf.baseDir + conf.externalDataDir;
		}

		this.usersURL = this.setTrailingSlash(
				config.WebServices.userIP) + 
				config.WebServices.userRootURI;

		this.adminwsURL = this.setTrailingSlash(
				config.WebServices.adminIP) + 
				config.WebServices.adminRootURI;

		console.log(this.baseDataDir, this.baseDir, this.usersURL );
	}

	// in order to make the configuration file more robust, check for leading and trailing "/"
	verifySystemConfigFormatting(config) {
		//
		// OperatorInterface
		//
		config.OperatorInterface.baseDir = 
			this.setTrailingSlash ( config.OperatorInterface.baseDir);

		config.OperatorInterface.externalDataDir = 
			this.removeLeadingSlash ( config.OperatorInterface.externalDataDir);

		config.OperatorInterface.externalDataDir = 
			this.setTrailingSlash ( config.OperatorInterface.externalDataDir);

		//
		// WebServices
		//
		config.WebServices.dataIP = this.removeTrailingSlash ( config.WebServices.dataIP);

		// this.appConfig.WebServices.userIP = this.removeTrailingSlash (this.appConfig.WebServices.userIP);

		//
		// Documentation
		//
		if ( config.hasOwnProperty ('Documentation')) {
			if ( config.Documentation.hasOwnProperty('IP')) {
				config.Documentation.IP = this.removeTrailingSlash (config.Documentation.IP);
			}
			if ( config.Documentation.hasOwnProperty ('userPath')) {
				config.Documentation.userPath = this.removeLeadingSlash (config.Documentation.userPath);
			}
		}

		//
		// OperatorInterfaceDemoMode
		//
		if ( config.hasOwnProperty('OperatorInterfaceDemoMode')) {
			config.OperatorInterfaceDemoMode.baseDir = 
				this.setTrailingSlash ( config.OperatorInterfaceDemoMode.baseDir);
			config.OperatorInterfaceDemoMode.dataDir = 
				this.removeLeadingSlash ( config.OperatorInterfaceDemoMode.dataDir);
			config.OperatorInterfaceDemoMode.dataDir = 
				this.setTrailingSlash ( config.OperatorInterfaceDemoMode.dataDir);
			config.OperatorInterfaceDemoMode.externalDataDir =
				this.removeLeadingSlash ( config.OperatorInterfaceDemoMode.externalDataDir);
			config.OperatorInterfaceDemoMode.externalDataDir =
				this.setTrailingSlash ( config.OperatorInterfaceDemoMode.externalDataDir);
		}
	}

	// helper function to verifySystemConfigFormatting - verifies last character in a string is a slash
	setTrailingSlash(tmpStr) {
		if ( tmpStr.length === 0 ) {
			return '';
		}
		if ( tmpStr[tmpStr.length - 1] === '/' ) {
			return tmpStr;
		}
		return tmpStr + '/';
	}
	// helper function to verifySystemConfigFormatting - verifies last character in a string is not a slash
	removeTrailingSlash(tmpStr) {
		if ( tmpStr.length === 0 ) {
			return '';
		}
		if ( tmpStr[tmpStr.length - 1] === '/' ) {
			return tmpStr.substring(0, tmpStr.length - 1);
		}
		return tmpStr;
	}
	// helper function to verifySystemConfigFormatting - verifies first character in a string is not a slash
	removeLeadingSlash(tmpStr) {
		if ( tmpStr.length === 0 ) {
			return '';
		}
		if ( tmpStr[0] === '/' ) {
			return tmpStr.substring(1);
		} else {
			return tmpStr;
		}
	}

	checkIfGuestAllowed() {
	}

	isDisclaimerApprovalRequired() {
		let disclaimerRequired = false; 
		let config = this.appConfig.OperatorInterface;
		if ( this.appConfig.DemoMode ) {
			config = this.appConfig.OperatorInterfaceDemoMode;
		}

		if ( config.hasOwnProperty("disclaimerApprovalRequired") &&
			config.disclaimerApprovalRequired == true ) {
				disclaimerRequired = true;
			}
	
		return disclaimerRequired;
	}

	getChartTrackerOptions(): any[] {
		return [
		{ label: 'Floating', value: tooltipPreferenceOptions.FLOATING },
		{ label: 'Fixed', value: tooltipPreferenceOptions.FIXED }
	];
	}

}
